import React, { useId, useState } from 'react';

/**
 * A ruled section: a heading with the size of what it holds in a bubble, a
 * hairline under it, then the content. The default way to divide a page —
 * a card is for something that genuinely is an object, like a table or a tile.
 *
 * `aside` is whatever belongs at the right end of the rule: a total, a
 * control, a timestamp.
 *
 * A `collapsible` section turns its whole head into the toggle, so the rule
 * line is the target rather than a chevron the size of a fingernail. It starts
 * closed unless `defaultOpen` says otherwise — a section that collapses is
 * usually one you open when you want it. A section that is not collapsible is
 * always open, whatever `defaultOpen` says.
 */
export default function Section({
  title,
  count = null,
  note = null,
  aside = null,
  collapsible = false,
  defaultOpen = false,
  children,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const isOpen = collapsible ? open : true;

  const heading = (
    <span className="bdc-section__heading">
      {collapsible ? (
        <i className="bi bi-chevron-down bdc-section__chevron" aria-hidden="true" />
      ) : null}
      <span className="bdc-section__title">{title}</span>
      {count !== null && count !== undefined ? (
        <span className="bdc-count">{count}</span>
      ) : null}
      {note ? <span className="bdc-section__note">{note}</span> : null}
    </span>
  );

  return (
    <section className="bdc-section">
      {collapsible ? (
        <button
          type="button"
          className="bdc-section__head bdc-section__head--toggle"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={isOpen}
          aria-controls={bodyId}
        >
          {heading}
          {aside}
        </button>
      ) : (
        <div className="bdc-section__head">
          {heading}
          {aside}
        </div>
      )}

      {/* The body stays mounted so its height can animate; `inert` keeps a
          closed section out of the tab order and off the accessibility tree. */}
      <div id={bodyId} className="bdc-section__body" data-open={isOpen ? 'true' : 'false'}>
        <div className="bdc-section__bodyinner" inert={isOpen ? undefined : ''}>
          {/* The clip and the padding have to be separate boxes: overflow
              clips at the PADDING edge, so padding on the clipping element is
              a band the content paints into before it is cut off - which is a
              strip of the first card still showing under a closed section. */}
          <div className="bdc-section__bodypad">{children}</div>
        </div>
      </div>
    </section>
  );
}
